import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PatientService from "../services/PatientService";
import BloodTypeService from "../services/BloodTypeService";

export default function PatientManagement() {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();

  const initialFilters = {
    firstName: urlParams.get("firstName") || "",
    lastName: urlParams.get("lastName") || "",
    phone: urlParams.get("phone") || "",
  };
  const initialPage = Number(urlParams.get("page")) || 1;

  // --- STATE ZA LISTU I PAGINACIJU ---
  const [patients, setPatients] = useState([]);
  const [totalPatients, setTotalPatients] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [loading, setLoading] = useState(false);
  const pageSize = 10;

  // --- STATE ZA FILTERE ---
  const [filters, setFilters] = useState(initialFilters);
  const [searchParams, setSearchParams] = useState(initialFilters);

  // Upiši trenutnu pretragu/stranu u URL da bi "Nazad" iz kartona vratio na istu pretragu
  const syncUrl = (nextFilters, nextPage) => {
    const params = {};
    if (nextFilters.firstName) params.firstName = nextFilters.firstName;
    if (nextFilters.lastName) params.lastName = nextFilters.lastName;
    if (nextFilters.phone) params.phone = nextFilters.phone;
    if (nextPage > 1) params.page = String(nextPage);
    setUrlParams(params);
  };

  // --- STATE ZA DODAVANJE I IZMENU ---
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false); // NOVO
  const [bloodTypes, setBloodTypes] = useState([]);
  
  const [newPatient, setNewPatient] = useState({
    name: "", surname: "", email: "", phone: "",
    birthDate: "", lbo: "", medicalNote: "",
    allergies: "", surgeries: "", otherInfo: "", bloodTypeID: ""
  });

  const [editingPatient, setEditingPatient] = useState(null); // NOVO

  // Zaključavanje skrola
  useEffect(() => {
    if (showAddPopup || showEditPopup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showAddPopup, showEditPopup]);

  useEffect(() => {
    fetchPatients();
  }, [currentPage, searchParams]);

  // Učitavanje krvnih grupa za oba modala
  useEffect(() => {
    if (showAddPopup || showEditPopup) {
      BloodTypeService.getAllBloodTypes()
        .then(data => setBloodTypes(data || []))
        .catch(err => console.error("Greška kod krvnih grupa:", err));
    }
  }, [showAddPopup, showEditPopup]);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const res = await PatientService.getPagedPatients(
        searchParams.firstName,
        searchParams.lastName,
        searchParams.phone,
        currentPage,
        pageSize
      );
      setPatients(res.data || []);
      setTotalPatients(res.total || 0); 
    } catch (err) {
      console.error("Greška pri učitavanju:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchParams({ ...filters });
    syncUrl(filters, 1);
  };

  const handleClearSearch = () => {
    const empty = { firstName: "", lastName: "", phone: "" };
    setFilters(empty);
    setSearchParams(empty);
    setCurrentPage(1);
    syncUrl(empty, 1);
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...newPatient,
      bloodType: newPatient.bloodTypeID ? { bloodID: parseInt(newPatient.bloodTypeID) } : null
    };

    PatientService.create(payload)
      .then(() => {
        setShowAddPopup(false);
        setNewPatient({ name: "", surname: "", email: "", phone: "", birthDate: "", lbo: "", medicalNote: "", allergies: "", surgeries: "", otherInfo: "", bloodTypeID: "" });
        fetchPatients();
      })
      .catch(err => alert("Greška pri kreiranju pacijenta."));
  };

  // NOVO: Slanje izmenjenih podataka
  const handleEditSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...editingPatient,
      bloodType: editingPatient.bloodTypeID ? { bloodID: parseInt(editingPatient.bloodTypeID) } : null
    };

    PatientService.update(editingPatient.patientID, payload)
      .then(() => {
        setShowEditPopup(false);
        setEditingPatient(null);
        fetchPatients();
      })
      .catch(err => alert("Greška pri izmeni podataka pacijenta."));
  };

  const handleDeletePatient = (p) => {
    if (!window.confirm(`Da li ste sigurni da želite da obrišete pacijenta ${p.name} ${p.surname}? Ova akcija je nepovratna.`)) return;

    PatientService.delete(p.patientID)
      .then(() => fetchPatients())
      .catch(err => {
        if (err.message.includes("409")) {
          alert("Pacijent ima postojeće posete i ne može biti obrisan.");
        } else {
          alert("Greška pri brisanju pacijenta.");
        }
      });
  };

  // NOVO: Otvaranje modala za izmenu
  const openEditModal = (p) => {
    setEditingPatient({
      ...p,
      bloodTypeID: p.bloodType?.bloodID || ""
    });
    setShowEditPopup(true);
  };

  const totalPages = Math.ceil(totalPatients / pageSize);

  const styles = {
    page: { padding: "40px", backgroundColor: "#f8f9fa", minHeight: "100vh", fontFamily: "'Segoe UI', sans-serif" },
    header: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
    searchContainer: { backgroundColor: "#fff", padding: "20px", borderRadius: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", marginBottom: "20px" },
    formInline: { display: "flex", gap: "10px", flexWrap: "wrap" },
    input: { padding: "10px", borderRadius: "6px", border: "1px solid #ddd", flex: 1, minWidth: "150px" },
    tableCard: { backgroundColor: "#fff", borderRadius: "10px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", overflow: "hidden" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { backgroundColor: "#2c3e50", color: "#fff", padding: "15px", textAlign: "left" },
    td: { padding: "15px", borderBottom: "1px solid #eee" },
    btnGreen: { backgroundColor: "#27ae60", color: "white", border: "none", padding: "10px 20px", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" },
    btnBlue: { backgroundColor: "#2980b9", color: "white", border: "none", padding: "10px 20px", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" },
    btnOrange: { backgroundColor: "#f39c12", color: "white", border: "none", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", fontWeight: "600" }, // NOVO
    btnRed: { backgroundColor: "#e74c3c", color: "white", border: "none", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", fontWeight: "600" },
    btnAction: { backgroundColor: "#3498db", color: "white", border: "none", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", fontWeight: "600" },
    pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", marginTop: "30px", padding: "10px" },
    pageInfo: { fontSize: "16px", fontWeight: "600", color: "#2c3e50" },
    modalOverlay: { 
        position: "fixed", top: 0, left: 0, width: "100%", height: "100%", 
        backgroundColor: "rgba(0,0,0,0.5)", display: "flex", 
        justifyContent: "center", alignItems: "flex-start", 
        zIndex: 1000, overflowY: "auto", padding: "40px 0" 
    },
    modalContent: { 
        backgroundColor: "#fff", padding: "30px", borderRadius: "12px", 
        width: "500px", boxShadow: "0 10px 25px rgba(0,0,0,0.2)", marginBottom: "40px" 
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2 style={{ color: "#2c3e50", margin: 0 }}>📋 Lista Zdravstvenih Kartona</h2>
        <button onClick={() => setShowAddPopup(true)} style={styles.btnGreen}>➕ Novi Pacijent</button>
      </div>

      <div style={styles.searchContainer}>
        <form onSubmit={handleSearchSubmit} style={styles.formInline}>
          <input style={styles.input} placeholder="Ime" value={filters.firstName} onChange={e => setFilters({...filters, firstName: e.target.value})} />
          <input style={styles.input} placeholder="Prezime" value={filters.lastName} onChange={e => setFilters({...filters, lastName: e.target.value})} />
          <input style={styles.input} placeholder="Telefon" value={filters.phone} onChange={e => setFilters({...filters, phone: e.target.value})} />
          <button type="submit" style={styles.btnBlue}>🔍 Pretraži</button>
          <button type="button" onClick={handleClearSearch} style={{ ...styles.btnAction, backgroundColor: "#6c757d" }}>✖️ Očisti</button>
        </form>
      </div>

   <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Ime i Prezime</th>
              <th style={styles.th}>Telefon</th>
              <th style={styles.th}>Datum rođenja</th> {/* DODATO */}
              <th style={styles.th}>LBO</th>
              <th style={styles.th}>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" style={{ textAlign: "center", padding: "30px" }}>Učitavanje podataka...</td></tr>
            ) : patients.length > 0 ? (
              patients.map(p => (
                <tr key={p.patientID}>
                  <td style={styles.td}><strong>{p.name} {p.surname}</strong></td>
                  <td style={styles.td}>{p.phone || "/"}</td>
                  {/* PRIKAZ DATUMA ROĐENJA */}
                  <td style={styles.td}>
                    {p.birthDate ? p.birthDate.split('T')[0] : "/"}
                  </td>
                  <td style={styles.td}>{p.lbo || "/"}</td>
                  <td style={styles.td}>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button 
                        onClick={() => navigate('/new-visit', { state: { selectedPatient: p } })}
                        style={{ ...styles.btnAction, backgroundColor: "#27ae60" }}
                      >
                        ➕ Nova Poseta
                      </button>
                      <button
                        onClick={() => navigate(`/patient-carton/${p.patientID}`)}
                        style={styles.btnAction}
                      >
                        📂 Karton
                      </button>
                      <button
                        onClick={() => navigate(`/patients/${p.patientID}/acupuncture-cards`)}
                        style={{ ...styles.btnAction, backgroundColor: "#B8860B" }}
                      >
                        📋 Karton akupunktura
                      </button>
                      <button
                        onClick={() => navigate(`/patients/${p.patientID}/estetska-medicina`)}
                        style={{ ...styles.btnAction, backgroundColor: "#8e44ad" }}
                      >
                        💆 Estetska medicina
                      </button>
                      <button
                        onClick={() => navigate('/izvestaj', { state: { patient: p } })}
                        style={{ ...styles.btnAction, backgroundColor: "#5a6268" }}
                      >
                        📝 Izveštaj
                      </button>
                    
                      <button
                        onClick={() => openEditModal(p)}
                        style={styles.btnOrange}
                      >
                        ✏️ Izmeni
                      </button>
                      <button
                        onClick={() => handleDeletePatient(p)}
                        style={styles.btnRed}
                      >
                        🗑️ Obriši
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="5" style={{ textAlign: "center", padding: "30px", color: "#999" }}>Nema pronađenih pacijenata.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINACIJA */}
      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button disabled={currentPage === 1} onClick={() => { const p = currentPage - 1; setCurrentPage(p); syncUrl(searchParams, p); }} style={{ ...styles.btnAction, backgroundColor: currentPage === 1 ? "#ccc" : "#3498db" }}>⬅️ Prethodna</button>
          <span style={styles.pageInfo}>Strana {currentPage} od {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => { const p = currentPage + 1; setCurrentPage(p); syncUrl(searchParams, p); }} style={{ ...styles.btnAction, backgroundColor: currentPage === totalPages ? "#ccc" : "#3498db" }}>Sledeća ➡️</button>
        </div>
      )}

     {/* POPUP ZA DODAVANJE */}
{showAddPopup && (
  <div style={styles.modalOverlay} onClick={() => setShowAddPopup(false)}>
    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
      <h3 style={{ marginTop: 0, borderBottom: "1px solid #eee", paddingBottom: "10px" }}>Novi Pacijent</h3>
      <form onSubmit={handleAddSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        
        {/* Ime i Prezime */}
        <div style={{ display: "flex", gap: "10px" }}>
          <input style={styles.input} placeholder="Ime" required onChange={e => setNewPatient({...newPatient, name: e.target.value})} />
          <input style={styles.input} placeholder="Prezime" required onChange={e => setNewPatient({...newPatient, surname: e.target.value})} />
        </div>

        <input style={styles.input} placeholder="Email" type="email" onChange={e => setNewPatient({...newPatient, email: e.target.value})} />
        <input style={styles.input} placeholder="Telefon" onChange={e => setNewPatient({...newPatient, phone: e.target.value})} />

        <div style={{ display: "flex", gap: "10px" }}>
          <input style={styles.input} type="date" onChange={e => setNewPatient({...newPatient, birthDate: e.target.value})} />
          <input style={styles.input} placeholder="LBO" onChange={e => setNewPatient({...newPatient, lbo: e.target.value})} />
        </div>

        {/* KRVNA GRUPA - Sklonjen 'required' */}
        <select 
          style={styles.input} 
          onChange={e => {
            const val = e.target.value;
            // Ako je prazno, šaljemo null backendu
            setNewPatient({...newPatient, bloodTypeID: val === "" ? null : val});
          }}
        >
          <option value="">-- Krvna grupa (opciono) --</option>
          {bloodTypes.map(bt => (
            <option key={bt.bloodID} value={bt.bloodID}>
              {bt.bloodTypeName}
            </option>
          ))}
        </select>

        <textarea 
          style={{...styles.input, minHeight: "60px"}} 
          placeholder="Alergije" 
          onChange={e => setNewPatient({...newPatient, allergies: e.target.value})} 
        />
        <textarea 
          style={{...styles.input, minHeight: "60px"}} 
          placeholder="Operacije" 
          onChange={e => setNewPatient({...newPatient, surgeries: e.target.value})} 
        />
        <textarea 
          style={{...styles.input, minHeight: "60px"}} 
          placeholder="Drugo / Hronična oboljenja" 
          onChange={e => setNewPatient({...newPatient, otherInfo: e.target.value})} 
        />

        <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
          <button type="submit" style={styles.btnGreen}>💾 Sačuvaj</button>
          <button type="button" style={{...styles.btnAction, backgroundColor: "#e74c3c"}} onClick={() => setShowAddPopup(false)}>Odustani</button>
        </div>
      </form>
    </div>
  </div>
)}

      {/* POPUP ZA IZMENU (NOVO) */}
      {showEditPopup && editingPatient && (
        <div style={styles.modalOverlay} onClick={() => { setShowEditPopup(false); setEditingPatient(null); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, borderBottom: "1px solid #f39c12", paddingBottom: "10px" }}>Izmeni Podatke</h3>
            <form onSubmit={handleEditSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ display: "flex", gap: "10px" }}>
                <input style={styles.input} value={editingPatient.name} placeholder="Ime" required onChange={e => setEditingPatient({...editingPatient, name: e.target.value})} />
                <input style={styles.input} value={editingPatient.surname} placeholder="Prezime" required onChange={e => setEditingPatient({...editingPatient, surname: e.target.value})} />
              </div>
              <input style={styles.input} value={editingPatient.email || ""} placeholder="Email" type="email" onChange={e => setEditingPatient({...editingPatient, email: e.target.value})} />
              <input style={styles.input} value={editingPatient.phone || ""} placeholder="Telefon" onChange={e => setEditingPatient({...editingPatient, phone: e.target.value})} />
              <div style={{ display: "flex", gap: "10px" }}>
                <input style={styles.input} type="date" value={editingPatient.birthDate ? editingPatient.birthDate.split('T')[0] : ""} onChange={e => setEditingPatient({...editingPatient, birthDate: e.target.value})} />
                <input style={styles.input} value={editingPatient.lbo || ""} placeholder="LBO" onChange={e => setEditingPatient({...editingPatient, lbo: e.target.value})} />
              </div>
              <select style={styles.input} value={editingPatient.bloodTypeID} required onChange={e => setEditingPatient({...editingPatient, bloodTypeID: e.target.value})}>
                <option value="">-- Izaberi krvnu grupu --</option>
                {bloodTypes.map(bt => <option key={bt.bloodID} value={bt.bloodID}>{bt.bloodTypeName}</option>)}
              </select>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
    <label style={{fontSize: "12px", color: "#666"}}>Medicinski podaci:</label>
    <textarea 
      style={{...styles.input, minHeight: "60px", backgroundColor: "#fff5f5"}} 
      value={editingPatient.allergies || ""} 
      placeholder="Alergije" 
      onChange={e => setEditingPatient({...editingPatient, allergies: e.target.value})} 
    />
    <textarea 
      style={{...styles.input, minHeight: "60px", backgroundColor: "#fdf8e3"}} 
      value={editingPatient.surgeries || ""} 
      placeholder="Operacije" 
      onChange={e => setEditingPatient({...editingPatient, surgeries: e.target.value})} 
    />
    <textarea 
      style={{...styles.input, minHeight: "60px", backgroundColor: "#f0fff4"}} 
      value={editingPatient.otherInfo || ""} 
      placeholder="Drugo / Hronično" 
      onChange={e => setEditingPatient({...editingPatient, otherInfo: e.target.value})} 
    />
</div>
              <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
                <button type="submit" style={styles.btnOrange}>💾 Sačuvaj Izmene</button>
                <button type="button" style={{...styles.btnAction, backgroundColor: "#e74c3c"}} onClick={() => { setShowEditPopup(false); setEditingPatient(null); }}>Odustani</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}